import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { I18nStore } from '@indutrust/shared-data';
import { SkeletonAvatarHeaderComponent } from '@indutrust/shared-ui';
import { Avatar } from 'primeng/avatar';
import { ButtonDirective } from 'primeng/button';
import { Card } from 'primeng/card';

@Component({
  selector: 'idt-card-main-navigate',
  imports: [
    Avatar,
    ButtonDirective,
    Card,
    RouterLink,
    SkeletonAvatarHeaderComponent,
  ],
  template: `
    <p-card
      [class]="
        'relative rounded-2xl shadow-md transition-all duration-300 ' +
        (isDisabled()
          ? 'cursor-default'
          : isLoading()
            ? 'cursor-wait'
            : 'cursor-pointer hover:-translate-y-0.5 hover:shadow-xl')
      "
      (click)="isDisabled() ? null : clicked.emit()"
      (keydown.enter)="isDisabled() ? null : clicked.emit()">
      <!-- Stretched anchor for native link behavior (ctrl+click, URL preview) -->
      @if (!isDisabled() && !isLoading() && route()) {
        <a
          class="absolute inset-0 z-10 no-underline outline-none
            focus-visible:ring-2 focus-visible:ring-primary
            focus-visible:ring-offset-2 rounded-2xl"
          [attr.aria-label]="title()"
          [routerLink]="route()"
          (click)="clicked.emit()"></a>
      }
      @if (isLoading()) {
        <idt-skeleton-avatar-header
          avatarSize="48px"
          class="p-6"
          [lines]="[{ width: '60%', height: '1.5rem' }, { width: '80%' }]" />
      } @else {
        <div
          class="flex flex-wrap items-center justify-center gap-4
            text-center sm:flex-nowrap sm:justify-start sm:text-start">
          @if (icon()) {
            <p-avatar
              class="idt-primary-glow shrink-0 text-primary-contrast!"
              size="large"
              [icon]="'pi pi-' + icon()"
              [style]="{
                background: 'var(--primary-color-gradient)',
              }" />
          }
          <div class="flex min-w-0 flex-col grow basis-full sm:basis-auto">
            @if (title()) {
              <p class="m-0 text-xl font-bold text-color text-pretty">
                {{ title() }}
              </p>
            }
            @if (description()) {
              <p class="m-0 text-muted-color leading-relaxed text-base">
                {{ description() }}
              </p>
            }
          </div>
          @if (buttonText()) {
            <a
              class="relative z-20 shrink-0 self-center sm:ms-auto"
              iconPos="right"
              pButton
              size="large"
              [rounded]="true"
              [routerLink]="isDisabled() || isLoading() ? null : route()"
              [tabindex]="isDisabled() ? -1 : 0">
              {{ buttonText() }}
              <i
                aria-hidden="true"
                [class]="
                  i18nStore.direction() === 'rtl'
                    ? 'pi pi-arrow-left'
                    : 'pi pi-arrow-right'
                "></i>
            </a>
          }
        </div>
      }
    </p-card>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Highlighted navigation card used to promote a primary action or feature.
 */
export class CardMainNavigateComponent {
  protected readonly i18nStore = inject(I18nStore);

  readonly buttonText = input<string>();
  readonly description = input<string>();
  readonly icon = input<string>();
  readonly isDisabled = input<boolean>();
  readonly isLoading = input<boolean>();
  readonly route = input<string[]>();
  readonly title = input<string>();
  readonly tooltip = input<string>();

  readonly clicked = output();
}
